<template>
  <Transition
    appear
    enter-active-class="transition-[transform,opacity,margin-bottom,margin-right] duration-400 ease-in-out"
    enter-from-class="translate-y-full md:translate-y-0 md:translate-x-full opacity-0"
    leave-active-class="transition-[transform,opacity,margin-bottom,margin-right] duration-200 ease-in-out"
    leave-to-class="translate-y-full md:translate-y-0 md:translate-x-full opacity-0"
    @after-leave="onTransitionComplete"
  >
    <div
      v-if="isVisible"
      :aria-describedby="aria.descriptionId"
      :aria-labelledby="aria.titleId"
      aria-modal="true"
      :class="wrapperClasses"
      role="dialog"
      :style="offsetStyle"
    >
      <div class="relative">
        <RevButtonIcon
          :aria-label="i18n(translations.closeLabel)"
          :class="closeButtonClasses"
          :icon="isNewBanner ? IconCrossSmall : IconCross"
          size="small"
          :variant="isNewBanner ? 'ghost' : 'primary'"
          @click="handleClose"
        />

        <!-- Hidden elements for screen readers -->
        <span :id="aria.titleId" class="sr-only">{{
          i18n(translations.ariaTitle)
        }}</span>
        <span :id="aria.descriptionId" class="sr-only">{{
          i18n(translations.ariaDescription)
        }}</span>

        <slot></slot>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { useRoute } from '#imports'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'
import {
  Breakpoint,
  BreakpointQueries,
} from '@backmarket/utils/dom/getCurrentBreakpoint'
import { tw } from '@backmarket/utils/string/tw'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconCross } from '@ds/icons/IconCross'
import { IconCrossSmall } from '@ds/icons/IconCrossSmall'
import { useMediaQuery } from '@vueuse/core'

import { CMS } from '~/scopes/cms/routes-names'
import { useShoppingAssistantPopupOffset } from '~/scopes/shopping-assistant/composables/useShoppingAssistantPopupOffset'

import translations from './BasePopup.translations'

const i18n = useI18n()
const route = useRoute()
const { isAppsFlyerUserConsentGiven } = useDataConsent()

const props = withDefaults(
  defineProps<{
    displayPopup: boolean
    bannerType?: 'old' | 'new'
  }>(),
  {
    displayPopup: false,
    bannerType: 'old',
  },
)

const emit = defineEmits(['closed'])

const aria = {
  titleId: 'lead-collection-aria-title-id',
  descriptionId: 'lead-collection-aria-description',
}

const isVisible = ref(false)

function show() {
  isVisible.value = true
}

function hide() {
  isVisible.value = false
}

function handleClose() {
  hide()
}

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isVisible.value) {
    handleClose()
  }
}

function onTransitionComplete() {
  if (!isVisible.value) {
    emit('closed')
  }
}

const isArticleRoute = computed(() => {
  return route.name === CMS.ARTICLE
})

const isNewBanner = computed(() => props.bannerType === 'new')

const closeButtonClasses = computed(() => {
  return [
    tw`absolute z-[1] bg-transparent`,
    isNewBanner.value &&
      tw`!text-static-default-low right-0 -translate-y-1/2 lg:!text-static-default-hi lg:right-4 lg:top-24`,
    !isNewBanner.value && tw`right-12 top-12`,
    isNewBanner.value &&
      isAppsFlyerUserConsentGiven.value &&
      tw`top-1/2 !w-auto mr-16 ml-8 !size-24 mood-inverse lg:mood-main`,
    isNewBanner.value &&
      !isAppsFlyerUserConsentGiven.value &&
      tw`right-6 top-24`,
  ]
})

const wrapperClasses = computed(() => {
  return [
    tw`shadow-long fixed z-10 transition-[transform,opacity,margin-bottom,margin-right] duration-300`,
    isNewBanner.value &&
      tw`lg:bottom-16 lg:right-16 rounded-lg bottom-16 max-lg:left-[calc(50%-16px)] max-lg:mx-16 max-lg:w-max max-lg:max-w-[calc(100vw-32px)] max-lg:-translate-x-1/2 lg:left-auto`,
    isNewBanner.value && isArticleRoute.value && tw`bottom-[70px]`,
    // Matches the new banner's own right-16 inset (see above).
    !isNewBanner.value && tw`w-full md:w-fit md:right-16`,
    !isNewBanner.value && isArticleRoute.value && tw`bottom-[70px]`,
    // Matches the new banner's own bottom-16 resting position (see above) so
    // the shopping-assistant offset (calibrated for a popup resting near the
    // viewport edge) doesn't stack on top of an already-elevated base spot.
    !isNewBanner.value && !isArticleRoute.value && tw`bottom-0 md:bottom-16`,
  ]
})

// Each banner only becomes a bottom-right corner card at its own snap
// breakpoint (old: `md`, new: `lg`) — below that it's centered/full-width,
// where clearing the FAB isn't a corner-overlap concern, so the vertical
// offset must not apply there.
const isMdDesktop = useMediaQuery(BreakpointQueries[Breakpoint.MD])
const isLgDesktop = useMediaQuery(BreakpointQueries[Breakpoint.LG])
const isDesktop = computed(() =>
  isNewBanner.value ? isLgDesktop.value : isMdDesktop.value,
)

// Additive on top of wrapperClasses' own bottom-*/right-* values via margin
// (rather than duplicating those pixel values here) so the popup clears the
// shopping-assistant FAB/side-panel regardless of which branch (old/new
// banner, article route or not) currently applies.
const { offsetStyle } = useShoppingAssistantPopupOffset(isDesktop)

watch(
  () => props.displayPopup,
  (displayPopup) => {
    const action = displayPopup ? show : hide
    action()
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    handleClose()
  },
)

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown)
})
</script>
